import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useRecoilValue } from 'recoil';

import { useSimpleBugoBrandListHook } from '@shared/api/bugoBrand/bugoBrand.hook';
import { BugoBrand } from '@shared/api/bugoBrand/bugoBrand.interface';
import { simpleBugoBrandListStateSelector } from '@shared/api/bugoBrand/bugoBrand.selector';
import { DistributionNetwork } from '@shared/api/distributionNetwork/distributionNetwork.interface';
import { FuneralHomeInfo } from '@shared/api/funeralHomeInfo/funeralHomeInfo.interface';
import { Shop } from '@shared/api/shop/shop.interface';
import { Button } from '@shared/components/Button';
import { queryString } from '@shared/hooks/recoil-query';
import {
  notificateError,
  notificateSuccess,
} from '@shared/plugIn/ant-notification/ant-notifiaction';
import { api_ } from '@shared/plugIn/axios';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { Input, Select, Spin, Table, TableColumnsType, Tag } from 'antd';

import { useAdminTitleHook } from '../hooks/useAdminTitleHook';

type BugoBrandSummary = {
  _id: string;
  name: string;
  totalFuneralHomes: number;
  connectedFuneralHomes: number;
  missingCount: number;
  duplicateCount: number;
  dnCount: number;
};

type DNWithPopulate = DistributionNetwork<string, FuneralHomeInfo, Shop>;

type ShopGroup = {
  shop: Shop;
  funeralHomeInfos: FuneralHomeInfo[];
};

type DuplicateEntry = {
  funeralHomeInfo: FuneralHomeInfo;
  dns: { dnId: string; shop: Shop }[];
};

const CollapsiblePanel: FC<{
  header: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
}> = ({ header, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded border border-gray-200">
      <button
        type="button"
        className="flex w-full items-center justify-between bg-gray-50 px-4 py-3 text-left hover:bg-gray-100"
        onClick={() => setOpen((v) => !v)}
      >
        <div>{header}</div>
        <span className="text-gray-400">{open ? '▲' : '▼'}</span>
      </button>
      {open && <div className="border-t border-gray-200 p-4">{children}</div>}
    </div>
  );
};

const AdminDistributionNetworkPage: FC = function AdminDistributionNetworkPage() {
  useAdminTitleHook('DN 관리');
  useSimpleBugoBrandListHook();

  const simpleBugoBrandState = useRecoilValue(simpleBugoBrandListStateSelector);
  const notificationInstance = useRecoilValue(notificationInstanceAtom);

  const [allFuneralHomes, setAllFuneralHomes] = useState<FuneralHomeInfo[]>([]);
  const [summaryList, setSummaryList] = useState<BugoBrandSummary[]>([]);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const [selectedBugoBrandId, setSelectedBugoBrandId] = useState<string | null>(null);
  const [dnList, setDnList] = useState<DNWithPopulate[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const [allShops, setAllShops] = useState<Shop[]>([]);

  const fetchAllFuneralHomes = useCallback(async () => {
    const { data } = await api_.get<FuneralHomeInfo[]>('admin/funeral-home-info');
    setAllFuneralHomes(data);
    return data;
  }, []);

  const fetchAllShops = useCallback(async () => {
    const { data } = await api_.get<Shop[]>('admin/shop');
    setAllShops(data);
    return data;
  }, []);

  const fetchSummary = useCallback(async () => {
    if (simpleBugoBrandState.status !== 'success' || !simpleBugoBrandState.data) return;

    setSummaryLoading(true);
    try {
      const [funeralHomes] = await Promise.all([
        fetchAllFuneralHomes(),
        allShops.length === 0 ? fetchAllShops() : Promise.resolve(allShops),
      ]);
      const totalCount = funeralHomes.length;

      const brands = simpleBugoBrandState.data;
      const summaries: BugoBrandSummary[] = await Promise.all(
        brands.map(async (brand) => {
          const { data: count } = await api_.get<number>(
            `admin/distribution-network/count?${queryString({
              filter: { bugoBrand: brand._id },
            })}`,
          );
          return {
            _id: brand._id,
            name: brand.bugoAgency?.corpName ?? brand._id,
            totalFuneralHomes: totalCount,
            connectedFuneralHomes: count,
            missingCount: totalCount - count,
            duplicateCount: 0,
            dnCount: count,
          };
        }),
      );
      setSummaryList(summaries);
    } catch (err) {
      console.error(err);
    } finally {
      setSummaryLoading(false);
    }
  }, [
    simpleBugoBrandState.status,
    simpleBugoBrandState.data,
    fetchAllFuneralHomes,
    fetchAllShops,
    allShops,
  ]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const fetchBugoBrandDetail = useCallback(async (bugoBrandId: string) => {
    setDetailLoading(true);
    try {
      const { data: dnListResult } = await api_.get<DNWithPopulate[]>(
        `admin/distribution-network/?${queryString({
          filter: { bugoBrand: bugoBrandId },
          populate: [{ path: 'funeralHomeInfo' }, { path: 'shop' }],
        })}`,
      );
      setDnList(dnListResult);
    } catch (err) {
      console.error(err);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const brandDetailRef = useRef<HTMLDivElement>(null);

  const handleBugoBrandSelect = useCallback(
    (bugoBrandId: string) => {
      setSelectedBugoBrandId(bugoBrandId);
      fetchBugoBrandDetail(bugoBrandId);
      setTimeout(() => {
        brandDetailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    },
    [fetchBugoBrandDetail],
  );

  const summaryColumns: TableColumnsType<BugoBrandSummary> = useMemo(
    () => [
      {
        title: '부고브랜드',
        dataIndex: 'name',
        key: 'name',
        sorter: (a, b) => a.name.localeCompare(b.name),
      },
      {
        title: '전체 장례식장',
        dataIndex: 'totalFuneralHomes',
        key: 'totalFuneralHomes',
        align: 'center' as const,
        width: 120,
      },
      {
        title: '연결됨',
        dataIndex: 'connectedFuneralHomes',
        key: 'connectedFuneralHomes',
        align: 'center' as const,
        width: 100,
        render: (val: number, record: BugoBrandSummary) => (
          <Tag color={val >= record.totalFuneralHomes ? 'green' : 'blue'}>{val}</Tag>
        ),
      },
      {
        title: '미연결',
        dataIndex: 'missingCount',
        key: 'missingCount',
        align: 'center' as const,
        width: 80,
        sorter: (a, b) => a.missingCount - b.missingCount,
        render: (val: number) => <Tag color={val === 0 ? 'green' : 'red'}>{val}</Tag>,
      },
      {
        title: '',
        key: 'action',
        width: 80,
        render: (_: unknown, record: BugoBrandSummary) => (
          <Button
            className="rounded bg-blue-500 px-3 py-1 text-sm text-white"
            onClick={() => handleBugoBrandSelect(record._id)}
          >
            상세
          </Button>
        ),
      },
    ],
    [handleBugoBrandSelect],
  );

  const { shopGroups, unconnectedFuneralHomes, duplicateEntries, uniqueConnectedCount } =
    useMemo(() => {
      const fhiDnMap = new Map<
        string,
        { fhi: FuneralHomeInfo; dns: { dnId: string; shop: Shop }[] }
      >();
      const shopGroupMap = new Map<string, ShopGroup>();

      dnList.forEach((dn) => {
        const shop = dn.shop as Shop;
        const fhi = dn.funeralHomeInfo as FuneralHomeInfo;
        if (!shop || !fhi) return;

        const existing = fhiDnMap.get(fhi._id);
        if (existing) {
          existing.dns.push({ dnId: dn._id, shop });
        } else {
          fhiDnMap.set(fhi._id, { fhi, dns: [{ dnId: dn._id, shop }] });
        }

        const shopGroup = shopGroupMap.get(shop._id);
        if (shopGroup) {
          shopGroup.funeralHomeInfos.push(fhi);
        } else {
          shopGroupMap.set(shop._id, { shop, funeralHomeInfos: [fhi] });
        }
      });

      const connectedFhiIds = new Set(fhiDnMap.keys());
      const unconnected = allFuneralHomes.filter((fh) => !connectedFhiIds.has(fh._id));

      const duplicates: DuplicateEntry[] = [];
      fhiDnMap.forEach(({ fhi, dns }) => {
        if (dns.length > 1) {
          duplicates.push({ funeralHomeInfo: fhi, dns });
        }
      });

      return {
        shopGroups: Array.from(shopGroupMap.values()),
        unconnectedFuneralHomes: unconnected,
        duplicateEntries: duplicates,
        uniqueConnectedCount: connectedFhiIds.size,
      };
    }, [dnList, allFuneralHomes]);

  const handleDeleteDn = useCallback(
    async (dnId: string) => {
      if (!selectedBugoBrandId) return;
      const confirmed = window.confirm('이 DN 연결을 삭제하시겠습니까?');
      if (!confirmed) return;

      try {
        await api_.delete(`admin/distribution-network/${dnId}`);
        notificateSuccess(notificationInstance, '삭제 완료');
        fetchBugoBrandDetail(selectedBugoBrandId);
        fetchSummary();
      } catch (err) {
        console.error(err);
        notificateError(notificationInstance, '삭제 실패');
      }
    },
    [selectedBugoBrandId, notificationInstance, fetchBugoBrandDetail, fetchSummary],
  );

  const handleShopChange = useCallback(
    async (funeralHomeInfoId: string, newShopId: string) => {
      if (!selectedBugoBrandId) return;

      const existingDn = dnList.find(
        (dn) =>
          (typeof dn.funeralHomeInfo === 'string'
            ? dn.funeralHomeInfo
            : dn.funeralHomeInfo._id) === funeralHomeInfoId,
      );

      try {
        if (existingDn) {
          await api_.patch(`admin/distribution-network/${existingDn._id}`, {
            shop: newShopId,
          });
        } else {
          await api_.post('admin/distribution-network', {
            bugoBrand: selectedBugoBrandId,
            funeralHomeInfo: funeralHomeInfoId,
            shop: newShopId,
          });
        }
        notificateSuccess(notificationInstance, '저장 완료');
        fetchBugoBrandDetail(selectedBugoBrandId);
        fetchSummary();
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : '알 수 없는 오류';
        console.error(err);
        notificateError(notificationInstance, '저장 실패', errorMsg);
      }
    },
    [
      selectedBugoBrandId,
      dnList,
      notificationInstance,
      fetchBugoBrandDetail,
      fetchSummary,
    ],
  );

  const handleBulkAssign = useCallback(
    async (shopId: string) => {
      if (!selectedBugoBrandId || unconnectedFuneralHomes.length === 0) return;

      const confirmed = window.confirm(
        `미연결 장례식장 ${unconnectedFuneralHomes.length}개를 선택한 Shop에 일괄 등록하시겠습니까?`,
      );
      if (!confirmed) return;

      try {
        await Promise.all(
          unconnectedFuneralHomes.map((fh) =>
            api_.post('admin/distribution-network', {
              bugoBrand: selectedBugoBrandId,
              funeralHomeInfo: fh._id,
              shop: shopId,
            }),
          ),
        );
        notificateSuccess(
          notificationInstance,
          `${unconnectedFuneralHomes.length}개 등록 완료`,
        );
        fetchBugoBrandDetail(selectedBugoBrandId);
        fetchSummary();
      } catch (err) {
        console.error(err);
        notificateError(notificationInstance, '일괄 등록 실패');
      }
    },
    [
      selectedBugoBrandId,
      unconnectedFuneralHomes,
      notificationInstance,
      fetchBugoBrandDetail,
      fetchSummary,
    ],
  );

  const selectedBrandName = useMemo(() => {
    if (!selectedBugoBrandId || !simpleBugoBrandState.data) return '';
    const brand = simpleBugoBrandState.data.find((b) => b._id === selectedBugoBrandId);
    return brand?.bugoAgency?.corpName ?? brand?._id ?? '';
  }, [selectedBugoBrandId, simpleBugoBrandState.data]);

  const shopSelectOptions = useMemo(
    () => allShops.map((s) => ({ label: s.name, value: s._id })),
    [allShops],
  );

  const funeralHomeColumns: TableColumnsType<FuneralHomeInfo> = useMemo(
    () => [
      {
        title: '장례식장명',
        dataIndex: 'name',
        key: 'name',
        sorter: (a, b) => a.name.localeCompare(b.name),
      },
      {
        title: '주소',
        dataIndex: 'address',
        key: 'address',
        ellipsis: true,
      },
      {
        title: '전화번호',
        dataIndex: 'phoneNumber',
        key: 'phoneNumber',
        width: 140,
      },
    ],
    [],
  );

  const unconnectedColumns: TableColumnsType<FuneralHomeInfo> = useMemo(
    () => [
      ...funeralHomeColumns,
      {
        title: 'Shop 배정',
        key: 'assignShop',
        width: 200,
        render: (_: unknown, record: FuneralHomeInfo) => (
          <Select
            placeholder="Shop 선택"
            options={shopSelectOptions}
            style={{ width: '100%' }}
            onChange={(shopId: string) => handleShopChange(record._id, shopId)}
            size="small"
          />
        ),
      },
    ],
    [funeralHomeColumns, shopSelectOptions, handleShopChange],
  );

  const connectedFhColumns: TableColumnsType<FuneralHomeInfo> = useMemo(
    () => [
      ...funeralHomeColumns,
      {
        title: 'Shop 변경',
        key: 'changeShop',
        width: 200,
        render: (_: unknown, record: FuneralHomeInfo) => {
          const currentDn = dnList.find(
            (dn) =>
              (typeof dn.funeralHomeInfo === 'string'
                ? dn.funeralHomeInfo
                : dn.funeralHomeInfo._id) === record._id,
          );
          const currentShopId =
            currentDn && typeof currentDn.shop !== 'string'
              ? currentDn.shop._id
              : undefined;

          return (
            <Select
              placeholder="Shop 선택"
              options={shopSelectOptions}
              defaultValue={currentShopId}
              style={{ width: '100%' }}
              onChange={(shopId: string) => handleShopChange(record._id, shopId)}
              size="small"
            />
          );
        },
      },
    ],
    [funeralHomeColumns, shopSelectOptions, handleShopChange, dnList],
  );

  const duplicateColumns: TableColumnsType<DuplicateEntry> = useMemo(
    () => [
      {
        title: '장례식장명',
        key: 'name',
        sorter: (a, b) => a.funeralHomeInfo.name.localeCompare(b.funeralHomeInfo.name),
        render: (_: unknown, record: DuplicateEntry) => record.funeralHomeInfo.name,
      },
      {
        title: '주소',
        key: 'address',
        ellipsis: true,
        render: (_: unknown, record: DuplicateEntry) => record.funeralHomeInfo.address,
      },
      {
        title: '연결된 Shop (DN 수)',
        key: 'shops',
        width: 300,
        render: (_: unknown, record: DuplicateEntry) => (
          <div className="flex flex-wrap gap-1">
            {record.dns.map((d) => (
              <Tag key={d.dnId} color="orange">
                {d.shop.name}
              </Tag>
            ))}
          </div>
        ),
      },
      {
        title: '중복 DN 삭제',
        key: 'actions',
        width: 300,
        render: (_: unknown, record: DuplicateEntry) => (
          <div className="flex flex-wrap gap-1">
            {record.dns.map((d) => (
              <Button
                key={d.dnId}
                className="rounded border border-red-300 bg-red-50 px-2 py-0.5 text-xs text-red-600 hover:bg-red-100"
                onClick={() => handleDeleteDn(d.dnId)}
              >
                {d.shop.name} 삭제
              </Button>
            ))}
          </div>
        ),
      },
    ],
    [handleDeleteDn],
  );

  const bugoBrandSelectOptions = useMemo(() => {
    if (simpleBugoBrandState.status !== 'success' || !simpleBugoBrandState.data)
      return [];
    return simpleBugoBrandState.data.map((brand) => ({
      label: brand.bugoAgency?.corpName ?? brand._id,
      value: brand._id,
    }));
  }, [simpleBugoBrandState.status, simpleBugoBrandState.data]);

  // ─── 장례식장 기준 검색 ───

  type FhiDnEntry = DistributionNetwork<BugoBrand, string, Shop>;

  const [fhiSearchText, setFhiSearchText] = useState('');
  const [fhiSearchResults, setFhiSearchResults] = useState<FuneralHomeInfo[]>([]);
  const [selectedFhiId, setSelectedFhiId] = useState<string | null>(null);
  const [selectedFhiName, setSelectedFhiName] = useState('');
  const [fhiDnList, setFhiDnList] = useState<FhiDnEntry[]>([]);
  const [fhiDnLoading, setFhiDnLoading] = useState(false);

  const handleFhiSearch = useCallback(() => {
    if (!fhiSearchText.trim()) {
      setFhiSearchResults([]);
      return;
    }
    const keyword = fhiSearchText.trim().toLowerCase();
    const results = allFuneralHomes.filter(
      (fh) =>
        fh.name.toLowerCase().includes(keyword) ||
        fh.address.toLowerCase().includes(keyword),
    );
    setFhiSearchResults(results);
  }, [fhiSearchText, allFuneralHomes]);

  useEffect(() => {
    if (allFuneralHomes.length === 0) {
      fetchAllFuneralHomes();
    }
  }, [allFuneralHomes.length, fetchAllFuneralHomes]);

  const fetchFhiDnDetail = useCallback(async (funeralHomeInfoId: string) => {
    setFhiDnLoading(true);
    try {
      const { data: dnResults } = await api_.get<FhiDnEntry[]>(
        `admin/distribution-network/?${queryString({
          filter: { funeralHomeInfo: funeralHomeInfoId },
          populate: [{ path: 'bugoBrand' }, { path: 'shop' }],
        })}`,
      );
      setFhiDnList(dnResults);
    } catch (err) {
      console.error(err);
    } finally {
      setFhiDnLoading(false);
    }
  }, []);

  const handleSelectFhi = useCallback(
    (fhi: FuneralHomeInfo) => {
      setSelectedFhiId(fhi._id);
      setSelectedFhiName(fhi.name);
      fetchFhiDnDetail(fhi._id);
    },
    [fetchFhiDnDetail],
  );

  const fhiConnectedBrands = useMemo(() => {
    if (!simpleBugoBrandState.data)
      return {
        connected: [] as FhiDnEntry[],
        missingBrands: [] as BugoBrand[],
        orphanDns: [] as FhiDnEntry[],
      };

    const orphanDns: FhiDnEntry[] = [];
    const validDns: FhiDnEntry[] = [];

    fhiDnList.forEach((dn) => {
      if (!dn.bugoBrand || (typeof dn.bugoBrand === 'string' && !dn.bugoBrand)) {
        orphanDns.push(dn);
      } else {
        validDns.push(dn);
      }
    });

    const connectedBrandIds = new Set(
      validDns.map((dn) =>
        typeof dn.bugoBrand === 'string' ? dn.bugoBrand : dn.bugoBrand._id,
      ),
    );
    const allBrands = simpleBugoBrandState.data;
    const missing = allBrands.filter((b) => !connectedBrandIds.has(b._id));
    return { connected: validDns, missingBrands: missing, orphanDns };
  }, [fhiDnList, simpleBugoBrandState.data]);

  const handleFhiShopChange = useCallback(
    async (dnId: string, newShopId: string) => {
      if (!selectedFhiId) return;
      try {
        await api_.patch(`admin/distribution-network/${dnId}`, { shop: newShopId });
        notificateSuccess(notificationInstance, '저장 완료');
        fetchFhiDnDetail(selectedFhiId);
      } catch (err) {
        console.error(err);
        notificateError(notificationInstance, '저장 실패');
      }
    },
    [selectedFhiId, notificationInstance, fetchFhiDnDetail],
  );

  const handleFhiNewDn = useCallback(
    async (bugoBrandId: string, shopId: string) => {
      if (!selectedFhiId) return;
      try {
        await api_.post('admin/distribution-network', {
          bugoBrand: bugoBrandId,
          funeralHomeInfo: selectedFhiId,
          shop: shopId,
        });
        notificateSuccess(notificationInstance, '등록 완료');
        fetchFhiDnDetail(selectedFhiId);
        fetchSummary();
      } catch (err) {
        console.error(err);
        notificateError(notificationInstance, '등록 실패');
      }
    },
    [selectedFhiId, notificationInstance, fetchFhiDnDetail, fetchSummary],
  );

  const handleFhiDeleteDn = useCallback(
    async (dnId: string) => {
      if (!selectedFhiId) return;
      const confirmed = window.confirm('이 DN 연결을 삭제하시겠습니까?');
      if (!confirmed) return;
      try {
        await api_.delete(`admin/distribution-network/${dnId}`);
        notificateSuccess(notificationInstance, '삭제 완료');
        fetchFhiDnDetail(selectedFhiId);
        fetchSummary();
      } catch (err) {
        console.error(err);
        notificateError(notificationInstance, '삭제 실패');
      }
    },
    [selectedFhiId, notificationInstance, fetchFhiDnDetail, fetchSummary],
  );

  const handleDeleteOrphanDns = useCallback(async () => {
    if (!selectedFhiId || fhiConnectedBrands.orphanDns.length === 0) return;
    const count = fhiConnectedBrands.orphanDns.length;
    const confirmed = window.confirm(
      `BugoBrand가 삭제된 고아 DN ${count}개를 일괄 삭제하시겠습니까?`,
    );
    if (!confirmed) return;

    try {
      await Promise.all(
        fhiConnectedBrands.orphanDns.map((dn) =>
          api_.delete(`admin/distribution-network/${dn._id}`),
        ),
      );
      notificateSuccess(notificationInstance, `${count}개 고아 DN 삭제 완료`);
      fetchFhiDnDetail(selectedFhiId);
      fetchSummary();
    } catch (err) {
      console.error(err);
      notificateError(notificationInstance, '고아 DN 삭제 실패');
    }
  }, [
    selectedFhiId,
    fhiConnectedBrands.orphanDns,
    notificationInstance,
    fetchFhiDnDetail,
    fetchSummary,
  ]);

  const handleFhiBulkShopChange = useCallback(
    async (newShopId: string) => {
      if (!selectedFhiId || fhiDnList.length === 0) return;
      const confirmed = window.confirm(
        `이 장례식장에 연결된 ${fhiDnList.length}개 부고브랜드의 Shop을 일괄 변경하시겠습니까?`,
      );
      if (!confirmed) return;

      try {
        await Promise.all(
          fhiDnList.map((dn) =>
            api_.patch(`admin/distribution-network/${dn._id}`, { shop: newShopId }),
          ),
        );
        notificateSuccess(notificationInstance, `${fhiDnList.length}개 일괄 변경 완료`);
        fetchFhiDnDetail(selectedFhiId);
        fetchSummary();
      } catch (err) {
        console.error(err);
        notificateError(notificationInstance, '일괄 변경 실패');
      }
    },
    [selectedFhiId, fhiDnList, notificationInstance, fetchFhiDnDetail, fetchSummary],
  );

  const fhiShopSelectOptions = useMemo(
    () => allShops.map((s) => ({ label: s.name, value: s._id })),
    [allShops],
  );

  const fhiConnectedColumns: TableColumnsType<FhiDnEntry> = useMemo(
    () => [
      {
        title: '부고브랜드',
        key: 'bugoBrand',
        sorter: (a, b) => {
          const nameA =
            typeof a.bugoBrand !== 'string'
              ? a.bugoBrand.bugoAgency?.corpName ?? a.bugoBrand._id
              : a.bugoBrand;
          const nameB =
            typeof b.bugoBrand !== 'string'
              ? b.bugoBrand.bugoAgency?.corpName ?? b.bugoBrand._id
              : b.bugoBrand;
          return nameA.localeCompare(nameB);
        },
        render: (_: unknown, record: FhiDnEntry) => {
          const brand = record.bugoBrand;
          if (typeof brand === 'string') return brand;
          return brand?.bugoAgency?.corpName ?? brand?._id;
        },
      },
      {
        title: 'Shop',
        key: 'shop',
        width: 200,
        render: (_: unknown, record: FhiDnEntry) => {
          const shop = record.shop;
          if (typeof shop === 'string') return shop;
          return shop.name;
        },
      },
      {
        title: 'Shop 변경',
        key: 'changeShop',
        width: 220,
        render: (_: unknown, record: FhiDnEntry) => {
          const currentShopId =
            typeof record.shop !== 'string' ? record.shop._id : undefined;
          return (
            <Select
              placeholder="Shop 선택"
              options={fhiShopSelectOptions}
              defaultValue={currentShopId}
              style={{ width: '100%' }}
              onChange={(shopId: string) => handleFhiShopChange(record._id, shopId)}
              size="small"
            />
          );
        },
      },
      {
        title: '',
        key: 'delete',
        width: 70,
        render: (_: unknown, record: FhiDnEntry) => (
          <Button
            className="rounded border border-red-300 bg-red-50 px-2 py-0.5 text-xs text-red-600"
            onClick={() => handleFhiDeleteDn(record._id)}
          >
            삭제
          </Button>
        ),
      },
    ],
    [fhiShopSelectOptions, handleFhiShopChange, handleFhiDeleteDn],
  );

  const fhiMissingColumns: TableColumnsType<BugoBrand> = useMemo(
    () => [
      {
        title: '부고브랜드',
        key: 'name',
        sorter: (a, b) =>
          (a.bugoAgency?.corpName ?? a._id).localeCompare(
            b.bugoAgency?.corpName ?? b._id,
          ),
        render: (_: unknown, record: BugoBrand) =>
          record.bugoAgency?.corpName ?? record._id,
      },
      {
        title: 'Shop 배정',
        key: 'assignShop',
        width: 220,
        render: (_: unknown, record: BugoBrand) => (
          <Select
            placeholder="Shop 선택"
            options={fhiShopSelectOptions}
            style={{ width: '100%' }}
            onChange={(shopId: string) => handleFhiNewDn(record._id, shopId)}
            size="small"
          />
        ),
      },
    ],
    [fhiShopSelectOptions, handleFhiNewDn],
  );

  return (
    <div className="space-y-6 p-4">
      <section>
        <h2 className="mb-3 text-lg font-bold">BugoBrand별 DN 연결 현황</h2>
        <Spin spinning={summaryLoading}>
          <Table
            columns={summaryColumns}
            dataSource={summaryList}
            rowKey="_id"
            size="small"
            bordered
            pagination={{ pageSize: 20, hideOnSinglePage: true }}
          />
        </Spin>
      </section>

      <section>
        <h2 className="mb-3 text-lg font-bold">장례식장 기준 DN 조회</h2>
        <div className="mb-4 flex items-center gap-2">
          <Input
            placeholder="장례식장명 또는 주소 검색"
            value={fhiSearchText}
            onChange={(e) => setFhiSearchText(e.target.value)}
            onPressEnter={handleFhiSearch}
            style={{ width: 300 }}
          />
          <Button
            className="rounded bg-blue-500 px-4 py-1.5 text-sm text-white"
            onClick={handleFhiSearch}
          >
            검색
          </Button>
        </div>

        {fhiSearchResults.length > 0 && !selectedFhiId && (
          <Table
            columns={[
              {
                title: '장례식장명',
                dataIndex: 'name',
                key: 'name',
              },
              {
                title: '주소',
                dataIndex: 'address',
                key: 'address',
                ellipsis: true,
              },
              {
                title: '',
                key: 'action',
                width: 80,
                render: (_: unknown, record: FuneralHomeInfo) => (
                  <Button
                    className="rounded bg-blue-500 px-3 py-1 text-sm text-white"
                    onClick={() => handleSelectFhi(record)}
                  >
                    선택
                  </Button>
                ),
              },
            ]}
            dataSource={fhiSearchResults}
            rowKey="_id"
            size="small"
            bordered
            pagination={{ pageSize: 10, hideOnSinglePage: true }}
          />
        )}

        {selectedFhiId && (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Tag color="purple" className="text-sm">
                {selectedFhiName}
              </Tag>
              <Button
                className="rounded border border-gray-300 px-3 py-1 text-sm"
                onClick={() => {
                  setSelectedFhiId(null);
                  setSelectedFhiName('');
                  setFhiDnList([]);
                }}
              >
                다른 장례식장 선택
              </Button>
            </div>

            <Spin spinning={fhiDnLoading}>
              {!fhiDnLoading && (
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-4 text-sm">
                    <span>
                      연결된 부고브랜드:{' '}
                      <Tag color="blue">{fhiConnectedBrands.connected.length}</Tag>
                    </span>
                    <span>
                      미연결 부고브랜드:{' '}
                      <Tag
                        color={
                          fhiConnectedBrands.missingBrands.length === 0 ? 'green' : 'red'
                        }
                      >
                        {fhiConnectedBrands.missingBrands.length}
                      </Tag>
                    </span>
                    {fhiConnectedBrands.orphanDns.length > 0 && (
                      <span>
                        삭제된 BugoBrand DN:{' '}
                        <Tag color="volcano">{fhiConnectedBrands.orphanDns.length}</Tag>
                      </span>
                    )}
                  </div>

                  {fhiConnectedBrands.orphanDns.length > 0 && (
                    <CollapsiblePanel
                      defaultOpen
                      header={
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-orange-700">
                            삭제된 BugoBrand의 고아 DN
                          </span>
                          <Tag color="volcano">
                            {fhiConnectedBrands.orphanDns.length}개
                          </Tag>
                        </div>
                      }
                    >
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <Button
                            className="rounded bg-red-500 px-4 py-1.5 text-sm text-white"
                            onClick={handleDeleteOrphanDns}
                          >
                            고아 DN {fhiConnectedBrands.orphanDns.length}개 일괄 삭제
                          </Button>
                        </div>
                        <Table
                          columns={[
                            {
                              title: 'DN ID',
                              dataIndex: '_id',
                              key: '_id',
                              ellipsis: true,
                            },
                            {
                              title: 'BugoBrand (원본 ID)',
                              key: 'bugoBrand',
                              render: (_: unknown, record: FhiDnEntry) => {
                                const b = record.bugoBrand;
                                if (!b) return <Tag color="red">null</Tag>;
                                if (typeof b === 'string')
                                  return <Tag color="red">{b}</Tag>;
                                return b._id;
                              },
                            },
                            {
                              title: 'Shop',
                              key: 'shop',
                              render: (_: unknown, record: FhiDnEntry) => {
                                const s = record.shop;
                                if (!s) return <Tag color="red">null</Tag>;
                                if (typeof s === 'string') return s;
                                return s.name;
                              },
                            },
                            {
                              title: '',
                              key: 'delete',
                              width: 70,
                              render: (_: unknown, record: FhiDnEntry) => (
                                <Button
                                  className="rounded border border-red-300 bg-red-50 px-2 py-0.5 text-xs text-red-600"
                                  onClick={() => handleFhiDeleteDn(record._id)}
                                >
                                  삭제
                                </Button>
                              ),
                            },
                          ]}
                          dataSource={fhiConnectedBrands.orphanDns}
                          rowKey="_id"
                          size="small"
                          pagination={{ pageSize: 50, hideOnSinglePage: true }}
                        />
                      </div>
                    </CollapsiblePanel>
                  )}

                  {fhiConnectedBrands.connected.length > 0 && (
                    <CollapsiblePanel
                      defaultOpen
                      header={
                        <div className="flex items-center gap-2">
                          <span className="font-medium">연결된 부고브랜드</span>
                          <Tag color="blue">{fhiConnectedBrands.connected.length}개</Tag>
                        </div>
                      }
                    >
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">일괄 Shop 변경:</span>
                          <Select
                            placeholder="Shop 선택"
                            options={fhiShopSelectOptions}
                            style={{ width: 200 }}
                            onChange={(shopId: string) => handleFhiBulkShopChange(shopId)}
                            size="small"
                            showSearch
                            filterOption={(input, option) =>
                              (option?.label as string)
                                ?.toLowerCase()
                                .includes(input.toLowerCase()) ?? false
                            }
                          />
                        </div>
                        <Table
                          columns={fhiConnectedColumns}
                          dataSource={fhiConnectedBrands.connected}
                          rowKey="_id"
                          size="small"
                          pagination={{ pageSize: 50, hideOnSinglePage: true }}
                        />
                      </div>
                    </CollapsiblePanel>
                  )}

                  {fhiConnectedBrands.missingBrands.length > 0 && (
                    <CollapsiblePanel
                      header={
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-red-600">
                            미연결 부고브랜드
                          </span>
                          <Tag color="red">
                            {fhiConnectedBrands.missingBrands.length}개
                          </Tag>
                        </div>
                      }
                    >
                      <Table
                        columns={fhiMissingColumns}
                        dataSource={fhiConnectedBrands.missingBrands}
                        rowKey="_id"
                        size="small"
                        pagination={{ pageSize: 50, hideOnSinglePage: true }}
                      />
                    </CollapsiblePanel>
                  )}
                </div>
              )}
            </Spin>
          </div>
        )}
      </section>

      <section ref={brandDetailRef}>
        <h2 className="mb-3 text-lg font-bold">BugoBrand 상세 조회</h2>
        <div className="mb-4 flex items-center gap-3">
          <Select
            placeholder="부고브랜드 선택"
            options={bugoBrandSelectOptions}
            style={{ width: 300 }}
            showSearch
            filterOption={(input, option) =>
              (option?.label as string)?.toLowerCase().includes(input.toLowerCase()) ??
              false
            }
            value={selectedBugoBrandId}
            onChange={handleBugoBrandSelect}
          />
          {selectedBrandName && (
            <span className="text-sm text-gray-500">
              선택됨: <strong>{selectedBrandName}</strong>
            </span>
          )}
        </div>

        {selectedBugoBrandId && (
          <Spin spinning={detailLoading}>
            {!detailLoading && (
              <div className="space-y-3">
                <div className="flex flex-wrap gap-4 text-sm">
                  <span>
                    전체 장례식장: <strong>{allFuneralHomes.length}</strong>
                  </span>
                  <span>
                    연결됨 (고유): <Tag color="blue">{uniqueConnectedCount}</Tag>
                  </span>
                  <span>
                    DN 레코드: <strong>{dnList.length}</strong>
                  </span>
                  <span>
                    중복 장례식장:{' '}
                    <Tag color={duplicateEntries.length === 0 ? 'green' : 'orange'}>
                      {duplicateEntries.length}
                    </Tag>
                  </span>
                  <span>
                    미연결:{' '}
                    <Tag color={unconnectedFuneralHomes.length === 0 ? 'green' : 'red'}>
                      {unconnectedFuneralHomes.length}
                    </Tag>
                  </span>
                </div>

                <div className="space-y-2">
                  {duplicateEntries.length > 0 && (
                    <CollapsiblePanel
                      defaultOpen
                      header={
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-orange-600">
                            중복 장례식장 (여러 Shop에 연결)
                          </span>
                          <Tag color="orange">{duplicateEntries.length}개</Tag>
                        </div>
                      }
                    >
                      <Table
                        columns={duplicateColumns}
                        dataSource={duplicateEntries}
                        rowKey={(record) => record.funeralHomeInfo._id}
                        size="small"
                        pagination={{ pageSize: 50, hideOnSinglePage: true }}
                      />
                    </CollapsiblePanel>
                  )}

                  {unconnectedFuneralHomes.length > 0 && (
                    <CollapsiblePanel
                      defaultOpen
                      header={
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-red-600">미연결 장례식장</span>
                          <Tag color="red">{unconnectedFuneralHomes.length}개</Tag>
                        </div>
                      }
                    >
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">일괄 배정:</span>
                          <Select
                            placeholder="Shop 선택"
                            options={shopSelectOptions}
                            style={{ width: 200 }}
                            onChange={(shopId: string) => handleBulkAssign(shopId)}
                            size="small"
                          />
                        </div>
                        <Table
                          columns={unconnectedColumns}
                          dataSource={unconnectedFuneralHomes}
                          rowKey="_id"
                          size="small"
                          pagination={{ pageSize: 50, hideOnSinglePage: true }}
                        />
                      </div>
                    </CollapsiblePanel>
                  )}

                  {shopGroups.map((group) => (
                    <CollapsiblePanel
                      key={group.shop._id}
                      header={
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{group.shop.name}</span>
                          <Tag color="blue">{group.funeralHomeInfos.length}개</Tag>
                        </div>
                      }
                    >
                      <Table
                        columns={connectedFhColumns}
                        dataSource={group.funeralHomeInfos}
                        rowKey="_id"
                        size="small"
                        pagination={{ pageSize: 50, hideOnSinglePage: true }}
                      />
                    </CollapsiblePanel>
                  ))}
                </div>
              </div>
            )}
          </Spin>
        )}
      </section>
    </div>
  );
};

export default AdminDistributionNetworkPage;
